import { imageUrlToBase64, printNumber, splitDate } from "src/utils";
import { NRA } from "src/calculator/utils";
import { useEffect, useState } from "react";
import { PrintClient } from "src/types";
import { calculateAge } from "../Info/PersonInfo";
import config from "src/services/config";
import { UserIcon } from "@heroicons/react/24/outline";
import PieChart from "../Charts/PieChart";

interface Props {
  client: PrintClient;
}

const Header = ({ color, text }: any) => {
  return (
    <div
      style={{ backgroundColor: color }}
      className="py-4 w-full flex justify-center uppercase text-white font-semibold mb-3 text-[25px]"
    >
      {text}
    </div>
  );
};

const Subtitle = ({ children }: any) => {
  return (
    <div className="font-semibold text-[27px] my-2 uppercase">{children}</div>
  );
};

const Container = ({ children, color, title }: any) => {
  return (
    <div className="border-2 rounded-lg mb-6" style={{ borderColor: color }}>
      <Header text={title} color={color} />
      <div className="m-5">{children}</div>
    </div>
  );
};

const AssetSummary = ({ client }: Props) => {
  const logoUrl = client?.userdata?.logo
    ? `${config.API_URL}logo?logo=${client?.userdata?.logo}`
    : `${config.APP_URL}/logo.png`;
  const [logoData, setLogoData] = useState(null as any);

  const getPerson = (person: number) => {
    return person == -1
      ? "Joint"
      : client.people.find((p) => p.id === person)?.name;
  };
  useEffect(() => {
    imageUrlToBase64(logoUrl).then((data) => {
      setLogoData(data);
    });
  }, [logoUrl]);

  const assetSummary = client.assetSummary;

  const cashAssets = assetSummary.cashAssets
    .map((i) => i.balance || 0)
    .reduce((a, b) => a + b, 0);
  const statementWealth = assetSummary.statementWealth
    .map((i) => i.marketValue || 0)
    .reduce((a, b) => a + b, 0);
  const hardAssets = assetSummary.hardAssets
    .map((i) => i.marketValue || 0)
    .reduce((a, b) => a + b, 0);

  const contractualWealth =
    assetSummary.lifeInsurance
      .map((i) => i.cashValue || 0)
      .reduce((a, b) => a + b, 0) +
    assetSummary.accumulationAnnuity
      .map((i) => i.accountValue || 0)
      .reduce((a, b) => a + b, 0) +
    assetSummary.personalPensionAnnuity
      .map((i) => i.accountValue || 0)
      .reduce((a, b) => a + b, 0);

  const assets = cashAssets + statementWealth + hardAssets + contractualWealth;

  const liabilities = assetSummary.debts
    .map((i) => i.balance || 0)
    .reduce((a, b) => a + b, 0);

  return (
    <div className="text-sm px-24 text-[22px]!">
      <div className="flex justify-end mb-4">
        <img
          src={`${logoData}`}
          alt="logo"
          style={{
            height: "50px",
            width: "auto",
            maxHeight: "50px",
            objectFit: "contain",
          }}
        />
      </div>
      <div className="border-2 border-[#1c3664] rounded-lg mb-4 w-full">
        <Header text="Asset Summary" color="#1c3664" />
        <div className="flex m-3 justify-between">
          <div className="whitespace-nowrap flex flex-col gap-4 my-5 mr-5">
            <div className="text-[40px] text-gray-800">{client.title}</div>
            <div className="text-[20px] italic text-gray-700">
              Prepared on {new Date().toLocaleDateString()}
            </div>
          </div>
          <div className="flex gap-3">
            {client.people.map((p) => (
              <div className="flex items-center justify-center gap-1 bg-gray-100 p-2 rounded-lg w-full max-w-[300px] h-full min-w-[200px] whitespace-nowrap">
                <div>
                  <UserIcon className="w-10 h-10 text-gray-600" />
                </div>
                <div className="flex flex-col gap-2 justify-center items-center ml-2 h-full">
                  <div className="text-[25px] whitespace-nowrap">
                    {p.name} ({calculateAge(new Date(p.birthday))})
                  </div>
                  <div className="text-[18px] text-gray-700">
                    {new Date(p.birthday).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* <table className="w-full">
            <tr>
              <td className="font-bold">First name</td>
              {client.people.map((person) => (
                <td className="text-center">{person.name}</td>
              ))}
            </tr>
            <tr>
              <td className="font-bold">Birthdate</td>
              {client.people.map((person) => (
                <td className="text-center">
                  {new Date(person.birthday).toLocaleDateString()}
                </td>
              ))}
            </tr>
            <tr>
              <td className="font-bold">Current Age</td>
              {client.people.map((person) => (
                <td className="text-center">
                  {calculateAge(new Date(person.birthday))}
                </td>
              ))}
            </tr>
          </table>*/}
        </div>
      </div>

      <Container color="#1c3664" title="Income/Cash">
        {assetSummary.income.length > 0 && (
          <>
            <Subtitle>Income</Subtitle>
            <table className="text-center border-collapse ">
              <thead className="border-b-2 border-black">
                <th className="px-4 py-2 text-left">Employer</th>
                <th className="px-4">Owner</th>
                <th className="px-4">Position</th>
                <th className="px-4">Annual Income</th>
              </thead>
              {client.assetSummary.income.map((income, i) => (
                <tr className={`${i % 2 === 0 ? "bg-gray-100" : ""}`}>
                  <td className=" px-4 py-2 text-left">{income.employer}</td>
                  <td className=" px-4">{getPerson(income.owner)}</td>
                  <td className=" px-4">{income.position}</td>
                  <td className=" px-4">{printNumber(income.annualAmount)}</td>
                </tr>
              ))}
              <tr>
                <td className="">{}</td>
                <td className="">{}</td>
                <td className="px-4 font-bold py-2">Total</td>

                <td className="px-4 font-bold">
                  {printNumber(
                    client.assetSummary.income
                      .map((i) => i.annualAmount)
                      .reduce((a, b) => a + b, 0),
                  )}
                </td>
              </tr>
            </table>
          </>
        )}

        {client.assetSummary.cashAssets.length > 0 && (
          <div className="">
            <Subtitle>Cash Assets</Subtitle>
            <table className="text-center border-collapse ">
              <thead className="border-b-2 border-black">
                <th className="px-4 py-2 text-left">Bank</th>
                <th className="px-4">Account #</th>
                <th className="px-4">Owner</th>
                <th className="px-4">Type</th>
                <th className="px-4">Interest Rate</th>
                <th className="px-4">Balance</th>
              </thead>
              {client.assetSummary.cashAssets.map((asset, i) => (
                <tr className={`${i % 2 === 0 ? "bg-gray-100" : ""}`}>
                  <td className="px-4 py-2 text-left">{asset.bank}</td>
                  <td className="px-4">{asset.accountNumber}</td>
                  <td className="px-4">{getPerson(asset.owner)}</td>
                  <td className="px-4">{asset.type}</td>
                  <td className="px-4">
                    {asset.interestRate !== undefined &&
                      `${asset.interestRate}%`}
                  </td>
                  <td className="px-4">{printNumber(asset.balance)}</td>
                </tr>
              ))}
              <tr>
                <td className="">{}</td>
                <td className="">{}</td>
                <td className="">{}</td>
                <td className="">{}</td>
                <td className="px-4 font-bold py-2">Total</td>
                <td className="px-4 font-bold">
                  {printNumber(
                    client.assetSummary.cashAssets
                      .map((a) => a.balance || 0)
                      .reduce((a, b) => a + b, 0),
                  )}
                </td>
              </tr>
            </table>
          </div>
        )}
      </Container>

      <div className="break-inside-avoid-page">
        <Container color="#4471c4" title="Social Insurance">
          <Subtitle>Social Security</Subtitle>
          <table className="text-center border-collapse">
            <thead className="border-b-2 border-black">
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4">Full Retirement Age</th>
              <th className="px-4">Full Retirement Date</th>
              <th className="px-4">Monthly Income</th>
              <th className="px-4">Annual Income</th>
            </thead>
            {client.assetSummary.socialInsurance.map((insurance, i) => {
              const person = client.people.find(
                (p) => p.id === insurance.owner,
              );
              if (!person) return null;
              const { year: birthYear } = splitDate(person?.birthday || "");
              const [retirementYear, retirementMonth] = NRA(birthYear);
              const birthday = new Date(person?.birthday || "");
              birthday.setMonth(birthday.getMonth() + retirementMonth);
              birthday.setFullYear(birthday.getFullYear() + retirementYear);

              return (
                <tr className={`${i % 2 === 0 ? "bg-gray-100" : ""}`}>
                  <td className="px-4 py-2 text-left">
                    {getPerson(insurance.owner)}
                  </td>
                  <td className="px-4">{`${retirementYear} years ${retirementMonth} months`}</td>
                  <td className="px-4">{birthday.toLocaleDateString()}</td>
                  <td className="px-4">
                    {printNumber(insurance.monthlyAmount)}
                  </td>
                  <td className="px-4">
                    {printNumber(insurance.monthlyAmount * 12)}
                  </td>
                </tr>
              );
            })}
            <tr>
              <td className="">{}</td>
              <td className="">{}</td>
              <td className="px-4 font-bold py-2">Total</td>
              <td className="px-4 font-bold">
                {printNumber(
                  client.assetSummary.socialInsurance
                    .map((i) => i.monthlyAmount)
                    .reduce((a, b) => a + b, 0),
                )}
              </td>
              <td className="px-4 font-bold">
                {printNumber(
                  client.assetSummary.socialInsurance
                    .map((i) => i.monthlyAmount * 12)
                    .reduce((a, b) => a + b, 0),
                )}
              </td>
            </tr>
          </table>
        </Container>
      </div>
      <div className="break-before-page"></div>

      {assetSummary.lifeInsurance.length > 0 ||
        assetSummary.longTermCare.length > 0 ||
        assetSummary.accumulationAnnuity.length > 0 ||
        assetSummary.personalPensionAnnuity.length > 0 ||
        assetSummary.pension.length > 0
        ? null
        : null}
      <Container color="#00b050" title="Contractual Wealth">
        <div className="">
          <div>
            {assetSummary.lifeInsurance.length > 0 && (
              <>
                <Subtitle>Life Insurance</Subtitle>
                <table className="text-center border-collapse">
                  <thead className="border-b-2 border-black">
                    <th className="px-4 py-2 text-left">Company</th>
                    <th className="px-4">Policy #</th>
                    <th className="px-4">Insured</th>
                    <th className="px-4">Type</th>
                    <th className="px-4">Annual Premium</th>
                    <th className="px-4">Cash Value</th>
                    <th className="px-4">Death Benefit</th>
                  </thead>
                  {client.assetSummary.lifeInsurance.map((insurance, i) => (
                    <tr className={`${i % 2 === 0 ? "bg-gray-100" : ""}`}>
                      <td className="px-4 py-2 text-left">
                        {insurance.company}
                      </td>
                      <td className="px-4">{insurance.policyNumber}</td>
                      <td className="px-4">{getPerson(insurance.insured)}</td>
                      <td className="px-4">{insurance.type}</td>
                      <td className="px-4">
                        {printNumber(insurance.annualPremium)}
                      </td>
                      <td className="px-4">
                        {printNumber(insurance.cashValue)}
                      </td>
                      <td className="px-4">
                        {printNumber(insurance.deathBenefit)}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className=""></td>
                    <td className=""></td>
                    <td className=""></td>
                    <td className="px-4 font-bold py-2">Total</td>
                    <td className="px-4 font-bold">
                      {printNumber(
                        client.assetSummary.lifeInsurance.reduce(
                          (sum, i) => sum + (i.annualPremium || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-4 font-bold">
                      {printNumber(
                        client.assetSummary.lifeInsurance.reduce(
                          (sum, i) => sum + (i.cashValue || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-4 font-bold">
                      {printNumber(
                        client.assetSummary.lifeInsurance.reduce(
                          (sum, i) => sum + (i.deathBenefit || 0),
                          0,
                        ),
                      )}
                    </td>
                  </tr>
                </table>
              </>
            )}

            {assetSummary.longTermCare.length > 0 && (
              <>
                <Subtitle>Long Term Care</Subtitle>
                <table className="text-center border-collapse">
                  <thead className="border-b-2 border-black">
                    <th className="px-4 py-2 text-left">Company</th>
                    <th className="px-4">Policy #</th>
                    <th className="px-4">Insured</th>
                    <th className="px-4">Elimination Period</th>
                    <th className="px-4">COLA</th>
                    <th className="px-4">Annual Premium</th>
                    <th className="px-4">Monthly Benefit</th>
                  </thead>
                  {client.assetSummary.longTermCare.map((care, i) => (
                    <tr className={`${i % 2 === 0 ? "bg-gray-100" : ""}`}>
                      <td className="px-4 py-2 text-left">{care.company}</td>
                      <td className="px-4">{care.policyNumber}</td>
                      <td className="px-4">{getPerson(care.insured)}</td>
                      <td className="px-4">{care.eliminationPeriod}</td>
                      <td className="px-4">{care.COLA}%</td>
                      <td className="px-4">
                        {printNumber(care.annualPremium)}
                      </td>
                      <td className="px-4">
                        {printNumber(care.monthlyBenefit)}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className=""></td>
                    <td className=""></td>
                    <td className=""></td>
                    <td className=""></td>
                    <td className="px-4 font-bold py-2">Total</td>
                    <td className="px-4 font-bold">
                      {printNumber(
                        client.assetSummary.longTermCare.reduce(
                          (sum, i) => sum + (i.annualPremium || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-4 font-bold">
                      {printNumber(
                        client.assetSummary.longTermCare.reduce(
                          (sum, i) => sum + (i.monthlyBenefit || 0),
                          0,
                        ),
                      )}
                    </td>
                  </tr>
                </table>
              </>
            )}

            {assetSummary.accumulationAnnuity.length > 0 && (
              <>
                <Subtitle>Accumulation Annuity</Subtitle>
                <table className="text-center border-collapse">
                  <thead className="border-b-2 border-black">
                    <th className="px-4 py-2 text-left">Company</th>
                    <th className="px-4">Policy #</th>
                    <th className="px-4">Owner</th>
                    <th className="px-4">Tax Status</th>
                    <th className="px-4">Type</th>
                    <th className="px-4">Account Value</th>
                  </thead>
                  {client.assetSummary.accumulationAnnuity.map((annuity, i) => (
                    <tr className={`${i % 2 === 0 ? "bg-gray-100" : ""}`}>
                      <td className="px-4 py-2 text-left">{annuity.company}</td>
                      <td className="px-4">{annuity.policyNumber}</td>
                      <td className="px-4">{getPerson(annuity.owner)}</td>
                      <td className="px-4">{annuity.taxStatus}</td>
                      <td className="px-4">{annuity.type}</td>
                      <td className="px-4">
                        {printNumber(annuity.accountValue)}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className=""></td>
                    <td className=""></td>
                    <td className=""></td>
                    <td className=""></td>
                    <td className="px-4 font-bold py-2">Total</td>
                    <td className="px-4 font-bold">
                      {printNumber(
                        client.assetSummary.accumulationAnnuity.reduce(
                          (sum, i) => sum + (i.accountValue || 0),
                          0,
                        ),
                      )}
                    </td>
                  </tr>
                </table>
              </>
            )}
          </div>
          <div>
            {assetSummary.personalPensionAnnuity.length > 0 && (
              <div className="break-inside-avoid-page">
                <Subtitle>Personal Pension Annuity</Subtitle>
                <table className="text-center border-collapse">
                  <thead className="border-b-2 border-black">
                    <th className="px-4 py-2 text-left">Company</th>
                    <th className="px-4">Policy #</th>
                    <th className="px-4">Owner</th>
                    <th className="px-4">Tax Status</th>
                    <th className="px-4">Effective Date</th>
                    <th className="px-4">Annual Income</th>
                    <th className="px-4">Account Value</th>
                  </thead>
                  {client.assetSummary.personalPensionAnnuity.map(
                    (annuity, i) => (
                      <tr className={`${i % 2 === 0 ? "bg-gray-100" : ""}`}>
                        <td className="px-4 py-2 text-left">
                          {annuity.company}
                        </td>
                        <td className="px-4">{annuity.policyNumber}</td>
                        <td className="px-4">{getPerson(annuity.owner)}</td>
                        <td className="px-4">{annuity.taxStatus}</td>
                        <td className="px-4">
                          {new Date(annuity.effectiveDate).toLocaleDateString()}
                        </td>
                        <td className="px-4">
                          {printNumber(annuity.annualIncome)}
                        </td>
                        <td className="px-4">
                          {printNumber(annuity.accountValue)}
                        </td>
                      </tr>
                    ),
                  )}
                  <tr>
                    <td className=""></td>
                    <td className=""></td>
                    <td className=""></td>
                    <td className=""></td>
                    <td className="px-4 py-2 font-bold">Total</td>
                    <td className="px-4 font-bold">
                      {printNumber(
                        client.assetSummary.personalPensionAnnuity.reduce(
                          (sum, i) => sum + (i.annualIncome || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-4 py-2 font-bold">
                      {printNumber(
                        client.assetSummary.personalPensionAnnuity.reduce(
                          (sum, i) => sum + (i.accountValue || 0),
                          0,
                        ),
                      )}
                    </td>
                  </tr>
                </table>
              </div>
            )}

            {assetSummary.pension.length > 0 && (
              <>
                <Subtitle>Pension</Subtitle>
                <table className="text-center border-collapse">
                  <thead className="border-b-2 border-black">
                    <th className="px-4 py-2 text-left">Company</th>
                    <th className="px-4">Account #</th>
                    <th className="px-4">Owner</th>
                    <th className="px-4">Monthly Income</th>
                    <th className="px-4">Annual Income</th>
                  </thead>
                  {client.assetSummary.pension.map((pension, i) => (
                    <tr className={`${i % 2 === 0 ? "bg-gray-100" : ""}`}>
                      <td className="px-4 py-2 text-left">{pension.company}</td>
                      <td className="px-4">{pension.accountNumber}</td>
                      <td className="px-4">{getPerson(pension.owner)}</td>
                      <td className="px-4">
                        {printNumber(pension.monthlyIncome)}
                      </td>
                      <td className="px-4">
                        {printNumber((pension.monthlyIncome || 0) * 12)}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className=""></td>
                    <td className=""></td>
                    <td className="px-4 py-2 font-bold">Total</td>
                    <td className="px-4 font-bold">
                      {printNumber(
                        client.assetSummary.pension.reduce(
                          (sum, i) => sum + (i.monthlyIncome || 0),
                          0,
                        ),
                      )}
                    </td>
                    <td className="px-4 font-bold">
                      {printNumber(
                        client.assetSummary.pension.reduce(
                          (sum, i) => sum + (i.monthlyIncome || 0) * 12,
                          0,
                        ),
                      )}
                    </td>
                  </tr>
                </table>
              </>
            )}
          </div>
        </div>
      </Container>

      <Container color="#c00000" title="Statement Wealth">
        <div className="break-inside-avoid-page">
          {assetSummary.statementWealth.filter((w) => w.qualified).length >
            0 && (
              <div className="break-inside-avoid-page">
                <Subtitle>Qualified </Subtitle>
                <table className="text-center border-collapse">
                  <thead className="border-b-2 border-black">
                    <th className="px-4 py-2 text-left">Company</th>
                    <th className="px-4">Account #</th>
                    <th className="px-4">Owner</th>
                    <th className="px-4">Type</th>
                    <th className="px-4">Managed</th>
                    <th className="px-4">Annual Contribution</th>
                    <th className="px-4">Market Value</th>
                  </thead>
                  {client.assetSummary.statementWealth
                    .filter((wealth) => wealth.qualified)
                    .map((wealth, i) => (
                      <tr className={`${i % 2 === 0 ? "bg-gray-100" : ""}`}>
                        <td className="px-4 py-2 text-left">{wealth.company}</td>
                        <td className="px-4">{wealth.accountNumber}</td>
                        <td className="px-4">{getPerson(wealth.owner)}</td>
                        <td className="px-4">{wealth.type}</td>
                        <td className="px-4">{wealth.managed ? "Yes" : "No"}</td>
                        <td className="px-4">
                          {printNumber(wealth.annualContribution)}
                        </td>
                        <td className="px-4">
                          {printNumber(wealth.marketValue)}
                        </td>
                      </tr>
                    ))}
                  <tr>
                    <td className=""></td>
                    <td className=""></td>
                    <td className=""></td>
                    <td className=""></td>
                    <td className="px-4 py-2 font-bold">Total</td>
                    <td className="px-4 font-bold">
                      {printNumber(
                        client.assetSummary.statementWealth
                          .filter((w) => w.qualified)
                          .reduce(
                            (sum, w) => sum + (w.annualContribution || 0),
                            0,
                          ),
                      )}
                    </td>
                    <td className="px-4 font-bold">
                      {printNumber(
                        client.assetSummary.statementWealth
                          .filter((w) => w.qualified)
                          .reduce((sum, w) => sum + (w.marketValue || 0), 0),
                      )}
                    </td>
                  </tr>
                </table>
              </div>
            )}
        </div>
        {assetSummary.statementWealth.filter((w) => !w.qualified).length >
          0 && (
            <div className="break-inside-avoid-page">
              <Subtitle>Non-Qualified </Subtitle>
              <table className="text-center  border-collapse">
                <thead className="border-b-2 border-black">
                  <th className="px-4 py-2 text-left">Company</th>
                  <th className="px-4">Account #</th>
                  <th className="px-4">Owner</th>
                  <th className="px-4">Type</th>
                  <th className="px-4">Managed</th>
                  <th className="px-4">Annual Contribution</th>
                  <th className="px-4">Market Value</th>
                </thead>
                {client.assetSummary.statementWealth
                  .filter((wealth) => !wealth.qualified)
                  .map((wealth, i) => (
                    <tr className={`${i % 2 === 0 ? "bg-gray-100" : ""}`}>
                      <td className="px-4 py-2 text-left">{wealth.company}</td>
                      <td className="px-4">{wealth.accountNumber}</td>
                      <td className="px-4">{getPerson(wealth.owner)}</td>
                      <td className="px-4">{wealth.type}</td>
                      <td className="px-4">{wealth.managed ? "Yes" : "No"}</td>
                      <td className="px-4">
                        {printNumber(wealth.annualContribution)}
                      </td>
                      <td className="px-4">{printNumber(wealth.marketValue)}</td>
                    </tr>
                  ))}
                <tr>
                  <td className=""></td>
                  <td className=""></td>
                  <td className=""></td>
                  <td className=""></td>
                  <td className="px-4 font-bold py-2">Total</td>
                  <td className="px-4 font-bold">
                    {printNumber(
                      client.assetSummary.statementWealth
                        .filter((w) => !w.qualified)
                        .reduce((sum, w) => sum + (w.annualContribution || 0), 0),
                    )}
                  </td>
                  <td className="px-4 font-bold">
                    {printNumber(
                      client.assetSummary.statementWealth
                        .filter((w) => !w.qualified)
                        .reduce((sum, w) => sum + (w.marketValue || 0), 0),
                    )}
                  </td>
                </tr>
              </table>
            </div>
          )}
      </Container>

      <Container color="#4471c4" title="Hard Assets">
        {assetSummary.hardAssets.length > 0 && (
          <div className="break-inside-avoid-page">
            <Subtitle>Hard Assets</Subtitle>
            <table className="text-center border-collapse">
              <thead className="border-b-2 border-black">
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4">Type</th>
                <th className="px-4">Owner</th>
                <th className="px-4">Cost Basis</th>
                <th className="px-4">Net Income</th>
                <th className="px-4">Debt</th>
                <th className="px-4">Market Value</th>
              </thead>
              {client.assetSummary.hardAssets.map((asset, i) => (
                <tr className={`${i % 2 === 0 ? "bg-gray-100" : ""}`}>
                  <td className="px-4 py-2 text-left">{asset.name}</td>
                  <td className="px-4">{asset.type}</td>
                  <td className="px-4">{getPerson(asset.owner)}</td>
                  <td className="px-4">{printNumber(asset.costBasis)}</td>
                  <td className="px-4">{printNumber(asset.netIncome)}</td>

                  <td className="px-4">
                    {printNumber(
                      assetSummary.debts
                        .filter((i) => i.asset == asset.id)
                        .map((i) => i.balance)
                        .reduce((a, b) => a + b, 0),
                    )}
                  </td>
                  <td className="px-4">{printNumber(asset.marketValue)}</td>
                </tr>
              ))}
              <tr>
                <td className=""></td>
                <td className=""></td>
                <td className=""></td>
                <td className="px-4 font-bold py-2">Total</td>

                <td className="px-4 font-bold">
                  {printNumber(
                    client.assetSummary.hardAssets.reduce(
                      (sum, a) => sum + (a.netIncome || 0),
                      0,
                    ),
                  )}
                </td>
                <td className="px-4 font-bold">
                  {printNumber(
                    assetSummary.debts
                      .filter((i) => i.asset)
                      .map((i) => i.balance)
                      .reduce((a, b) => a + b, 0),
                  )}
                </td>
                <td className="px-4 font-bold">
                  {printNumber(
                    client.assetSummary.hardAssets.reduce(
                      (sum, a) => sum + (a.marketValue || 0),
                      0,
                    ),
                  )}
                </td>
              </tr>
            </table>
          </div>
        )}
        {assetSummary.debts.length > 0 && (
          <div className="break-inside-avoid-page py-1">
            <Subtitle>Debts</Subtitle>
            <table className="text-center  border-collapse">
              <thead className="border-b-2 border-black">
                <th className="px-4 py-2 text-left">Lender</th>
                <th className="px-4">Asset</th>
                <th className="px-4">Type</th>
                <th className="px-4">Account #</th>
                <th className="px-4">Interest Rate</th>
                <th className="px-4">Monthly Payment</th>
                <th className="px-4">Balance</th>
              </thead>
              {assetSummary.debts.map((asset, i) => (
                <tr className={`${i % 2 === 0 ? "bg-gray-100" : ""}`}>
                  <td className="px-4 py-2 text-left">{asset.lender}</td>
                  <td className="px-4">
                    {
                      assetSummary.hardAssets.find((i) => i.id == asset.asset)
                        ?.name
                    }
                  </td>
                  <td className="px-4">{asset.type}</td>
                  <td className="px-4">{asset.accountNumber}</td>
                  <td className="px-4">{asset.interestRate}%</td>
                  <td className="px-4">{printNumber(asset.monthlyPayment)}</td>
                  <td className="px-4">{printNumber(asset.balance)}</td>
                </tr>
              ))}
              <tr>
                <td className=""></td>
                <td className=""></td>
                <td className=""></td>
                <td className=""></td>
                <td className="px-4 font-bold py-2">Total</td>
                <td className="px-4 font-bold">
                  {printNumber(
                    client.assetSummary.debts.reduce(
                      (sum, a) => sum + (a.monthlyPayment || 0),
                      0,
                    ),
                  )}
                </td>
                <td className="px-4 font-bold">
                  {printNumber(
                    client.assetSummary.debts.reduce(
                      (sum, a) => sum + (a.balance || 0),
                      0,
                    ),
                  )}
                </td>
              </tr>
            </table>
          </div>
        )}

        {assetSummary.inheritance.length > 0 && (
          <div className="break-inside-avoid-page">
            <Subtitle>Inheritances</Subtitle>
            <table className="text-center border-collapse">
              <thead className="border-b-2 border-black">
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4">Type</th>
                <th className="px-4">Amount</th>
              </thead>
              {assetSummary.inheritance.map((asset, i) => (
                <tr className={`${i % 2 === 0 ? "bg-gray-100" : ""}`}>
                  <td className="px-4 py-2 text-left">{asset.name}</td>
                  <td className="px-4">{asset.type}</td>
                  <td className="px-4">{printNumber(asset.amount)}</td>
                </tr>
              ))}
              <tr>
                <td className=""></td>
                <td className="px-4 font-bold">Total</td>
                <td className="px-4 font-bold py-2">
                  {printNumber(
                    client.assetSummary.inheritance.reduce(
                      (sum, a) => sum + (a.amount || 0),
                      0,
                    ),
                  )}
                </td>
              </tr>
            </table>
          </div>
        )}
      </Container>
      <div className="break-before-page"></div>
      <Container color="#4471c4" title="Analysis">
        <div className="break-inside-avoid-page">
          <div className="flex justify-between items-center  gap-4 pt-5 text-[24px]!">
            <div className="rounded-md flex flex-col gap-12 w-[700px]">
              <table className="text-center border-collapse">
                <thead className={`text-left z-50 border `}>
                  <tr className="border-b-2 border-black">
                    <th className="px-4 py-2 text-left">Net Worth Summary</th>
                    <th className="px-4 text-right">Amount ($)</th>
                  </tr>
                </thead>
                <tbody className="">
                  <tr className=" bg-gray-100">
                    <td className="px-4 py-2 text-left">Assets</td>
                    <td className="px-4  text-right">{printNumber(assets)}</td>
                  </tr>
                  <tr className="">
                    <td className="px-4 py-2 text-left">Liabilities</td>
                    <td className="px-4  text-right">
                      {printNumber(liabilities)}
                    </td>
                  </tr>
                  <tr className=" bg-gray-100 font-semibold">
                    <td className="px-4 py-2 text-left">Net Worth</td>
                    <td className="px-4  text-right">
                      {printNumber(assets - liabilities)}
                    </td>
                  </tr>
                </tbody>
              </table>
              <table className="text-center border-collapse">
                <thead
                  className={` cursor-pointer text-left sticky z-50 border font-normal!`}
                >
                  <tr className="border-b-2 border-black">
                    <th className="px-4 py-2">Asset Class - Summary</th>
                    <th className="px-4  text-right">Amount ($)</th>
                  </tr>
                </thead>
                <tbody className="">
                  <tr className=" bg-gray-100">
                    <td className="px-4 py-2 text-left">Cash</td>
                    <td className="px-4  text-right">
                      {printNumber(cashAssets)}
                    </td>
                  </tr>
                  <tr className="">
                    <td className="px-4 py-2 text-left">Contactual Wealth</td>
                    <td className="px-4  text-right">
                      {printNumber(contractualWealth)}
                    </td>
                  </tr>
                  <tr className=" bg-gray-100">
                    <td className="px-4 py-2 text-left">Statement Wealth</td>
                    <td className="px-4  text-right">
                      {printNumber(statementWealth)}
                    </td>
                  </tr>

                  <tr className="">
                    <td className="px-4 py-2 text-left">Hard Assets</td>
                    <td className="px-4  text-right">
                      {printNumber(hardAssets)}
                    </td>
                  </tr>
                  <tr className="font-semibold bg-gray-100">
                    <td className="px-4 py-2 text-left">Total Assets</td>
                    <td className="px-4  text-right">
                      {printNumber(
                        cashAssets +
                        contractualWealth +
                        statementWealth +
                        hardAssets,
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-8 mb-8 w-full flex items-center justify-center">
              <PieChart
                print
                data={[
                  {
                    label: "Cash",
                    value: cashAssets,
                  },
                  {
                    label: "Statement Wealth",
                    value: statementWealth,
                  },
                  {
                    label: "Hard Assets",
                    value: hardAssets,
                  },
                  {
                    label: "Contractual Wealth",
                    value: contractualWealth,
                  },
                ]}
              />
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default AssetSummary;
